/**
 * Lectura de la línea de empaque: texto pegado o escaneado → códigos.
 *
 * Toda la lógica está acá y es pura porque es la parte que se puede
 * equivocar en silencio: un separador mal tratado convierte dos etiquetas en
 * una sola cadena, el endpoint responde `invalid_code` y el operario no
 * entiende por qué la caja quedó a medio activar.
 *
 * Lo que NO hace esta función, a propósito: decidir si un código es válido.
 * El HMAC vive en el servidor (`services/shared/codes.py`) y un código bien
 * formado puede estar perfectamente forjado. Acá sólo se separa lo que puede
 * viajar en el cuerpo del request de lo que ni siquiera entra en el contrato
 * —`codes` es `str` de 20 a 24 caracteres— para no convertir una lectura
 * sucia en un 422 que tira el lote entero.
 */

/** Longitud del código impreso. Ver `CODE_LEN` en `services/shared/codes.py`. */
export const CODE_LENGTH = 20;

/** Rango que acepta `ActivationBulkIn.codes`. Menos que esto es un 422. */
const WIRE_MIN = 20;
const WIRE_MAX = 24;

export interface ScannedCodes {
  /** Listos para enviar, en orden de lectura y sin repetidos. */
  codes: string[];
  /** Leídos dos veces en la misma tanda. El servidor también los cuenta. */
  duplicates: string[];
  /** No entran en el contrato: lectura partida, ruido del scanner, basura. */
  unreadable: string[];
}

/**
 * Normaliza como lo hace el servidor antes de `parse_code`: recorta, pasa a
 * mayúsculas y saca los separadores que agrega un lector de códigos o una
 * planilla (espacios y guiones).
 */
export function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s-]+/g, '');
}

/**
 * Separa el pegado en tokens. Sirve tanto para "un código por línea" como
 * para una lista separada por comas, punto y coma o tabulaciones.
 */
export function parseScan(input: string): ScannedCodes {
  const codes: string[] = [];
  const duplicates: string[] = [];
  const unreadable: string[] = [];
  const seen = new Set<string>();

  for (const token of input.split(/[\s,;]+/)) {
    const code = normalizeCode(token);
    if (code.length === 0) continue;
    if (code.length < WIRE_MIN || code.length > WIRE_MAX) {
      unreadable.push(code);
      continue;
    }
    if (seen.has(code)) {
      duplicates.push(code);
      continue;
    }
    seen.add(code);
    codes.push(code);
  }

  return { codes, duplicates, unreadable };
}

/**
 * `07VZ…K165` → `07VZ…165` para pantalla. Un código completo en una tabla no
 * aporta —nadie lo lee de memoria— y ocupa el ancho de tres columnas.
 * El valor entero sigue disponible con `title`/copiar.
 */
export function shortCode(code: string): string {
  return code.length <= 12 ? code : `${code.slice(0, 6)}…${code.slice(-4)}`;
}

/**
 * Clave de idempotencia sugerida: `LINEA-3/2026-07-26/0007`.
 *
 * La elige el cliente y el UNIQUE `(tenant_id, client_batch_id)` la hace
 * valer. Con este formato, reenviar el turno de una línea después de un corte
 * de red cae exactamente sobre la misma clave y el servidor devuelve el
 * resultado guardado en vez de activar de nuevo.
 */
export function suggestBatchKey(line: string, sequence: number, now: Date): string {
  const day = now.toISOString().slice(0, 10);
  const safeLine = normalizeCode(line) || 'LINEA';
  return `${safeLine}/${day}/${String(sequence).padStart(4, '0')}`;
}
